/**
 * 十进制金额字符串精度工具（单一真源）。
 *
 * 迁移来源（FE-E1a / FE2-004）：merchant 与 customer 的 compareDecimal/isPositiveDecimal
 * 经逐字 diff 确认完全一致；sumDecimals 仅 merchant 有正式实现——三函数均以
 * web/merchant/src/lib/format.ts 为底本逐字搬运。
 * 已知差异（不强并，保留本地）：admin DepositReview.tsx 内有页面私有 sumDecimals 变体
 * （固定 scale=8、入参 string[]、非法项抛错而非跳过），契约不同，留在原地。
 *
 * 注意：金额 JSON 一律 string；业务真值路径禁止 parseFloat 比较（见各端 format 壳注释）。
 */

/**
 * 固定 2 位小数的法币集合（与后端 `TransferController.TWO_DECIMAL_FIAT` 完全一致）。
 * 前端币种精度的单一真源：其它模块不得另造币种表。
 */
export const TWO_DECIMAL_FIAT: ReadonlySet<string> = new Set([
  'USD',
  'EUR',
  'GBP',
  'CNY',
  'HKD',
  'SGD',
  'AUD',
  'CAD',
]);

/** 币种小数位上限：fiat → 2；其余（含空/未知币种）→ 8（后端定点上限） */
export function currencyFractionDigits(currency?: string | null): 2 | 8 {
  const code = String(currency ?? '').trim().toUpperCase();
  return TWO_DECIMAL_FIAT.has(code) ? 2 : 8;
}

/**
 * 金额是否满足币种小数位约束（业务校验用；禁止 parseFloat）。
 * 只认十进制字符串：空串 / 负数 / 科学计数法 / `1.` 一律 false。
 */
export function amountFitsCurrencyScale(amount: string, currency?: string | null): boolean {
  const raw = String(amount ?? '').trim();
  if (!/^\d+(\.\d+)?$/.test(raw)) {
    return false;
  }
  const fraction = raw.split('.')[1] ?? '';
  return fraction.length <= currencyFractionDigits(currency);
}

/** 金额为正数且满足币种小数位约束（划转前置校验真源） */
export function isPositiveAmountForCurrency(amount: string, currency?: string | null): boolean {
  const raw = String(amount ?? '').trim();
  return amountFitsCurrencyScale(raw, currency) && isPositiveDecimal(raw);
}

/**
 * 十进制金额字符串比较（业务真值路径；禁止 parseFloat 比较）。
 * @returns -1 | 0 | 1；非法格式返回 null
 */
export function compareDecimal(a: string, b: string): number | null {
  const parts = (raw: string) => {
    const t = raw.trim();
    if (!/^-?\d+(\.\d+)?$/.test(t)) {
      return null;
    }
    const neg = t.startsWith('-');
    const abs = neg ? t.slice(1) : t;
    const [ipRaw, fp = ''] = abs.split('.');
    const ip = ipRaw.replace(/^0+(?=\d)/, '') || '0';
    return { neg, ip, fp };
  };
  const A = parts(a);
  const B = parts(b);
  if (!A || !B) {
    return null;
  }
  const aZero = A.ip === '0' && !/[1-9]/.test(A.fp);
  const bZero = B.ip === '0' && !/[1-9]/.test(B.fp);
  if (aZero && bZero) {
    return 0;
  }
  if (A.neg !== B.neg) {
    return A.neg ? -1 : 1;
  }
  const scale = Math.max(A.fp.length, B.fp.length);
  const sa = A.ip + A.fp.padEnd(scale, '0');
  const sb = B.ip + B.fp.padEnd(scale, '0');
  let cmp = 0;
  if (sa.length !== sb.length) {
    cmp = sa.length < sb.length ? -1 : 1;
  } else if (sa < sb) {
    cmp = -1;
  } else if (sa > sb) {
    cmp = 1;
  }
  return A.neg ? (-cmp as -1 | 0 | 1) : cmp;
}

/** a > 0（业务校验用） */
export function isPositiveDecimal(a: string): boolean {
  const c = compareDecimal(a, '0');
  return c != null && c > 0;
}

/**
 * 安全累加 decimal 字符串（展示 KPI 汇总；以最长小数位对齐整数运算）。
 * 非法项跳过。
 */
export function sumDecimals(values: Array<string | null | undefined>): string {
  let maxScale = 0;
  const parsed: Array<{ neg: boolean; ip: string; fp: string }> = [];
  for (const v of values) {
    if (v == null || v === '') {
      continue;
    }
    const t = String(v).trim();
    if (!/^-?\d+(\.\d+)?$/.test(t)) {
      continue;
    }
    const neg = t.startsWith('-');
    const abs = neg ? t.slice(1) : t;
    const [ip, fp = ''] = abs.split('.');
    maxScale = Math.max(maxScale, fp.length);
    parsed.push({ neg, ip, fp });
  }
  let sum = 0n;
  for (const p of parsed) {
    const scaled = BigInt(p.ip + p.fp.padEnd(maxScale, '0'));
    sum += p.neg ? -scaled : scaled;
  }
  const neg = sum < 0n;
  const abs = neg ? -sum : sum;
  const s = abs.toString().padStart(maxScale + 1, '0');
  const whole = maxScale === 0 ? s : s.slice(0, -maxScale) || '0';
  const frac = maxScale === 0 ? '' : s.slice(-maxScale);
  return `${neg ? '-' : ''}${whole}${frac ? `.${frac}` : ''}`;
}
